import React from 'react';

import classnames from 'classnames2';

import { IBaseProps } from '../utils';

import styles from './Text.module.scss';

export interface ITextProps extends IBaseProps, React.HTMLAttributes<HTMLElement> {
  /** Changes the HTML element used to display the component. */
  as?: 'span' | 'p' | 'div';
  /** Changes the color of the text. This can also be done with classname or styles. */
  color?: 'default' | 'light' | 'white' | 'disabled' | 'link' | 'success' | 'error' | 'warning';
  /** Changes the font of the text. This can also be done with classname or styles. */
  font?: 'default' | 'condensed' | 'bold' | 'defaultalt' | 'semicondensed' | 'condensedalt';
  /** Changes the size of the text. This can also be done with classname or styles. */
  size?:
    | 'default'
    | 'small'
    | 'minimum'
    | 'medium'
    | 'large'
    | 'xlarge'
    | 'xxlarge'
    | 'xxxlarge'
    | 'maximum'
    | 'display'
    | 'displaylarge';
  /** Changes the weight of the text. This can also be done with classname or styles. */
  weight?: 'thin' | 'light' | 'book' | 'regular' | 'medium' | 'semibold' | 'bold';
  numeric?: boolean;
  inputMode?: 'none' | 'search' | 'numeric' | 'text' | 'email' | 'tel' | 'url' | 'decimal';
}

export const Text = React.forwardRef<HTMLParagraphElement, ITextProps>(
  ({ as, children, className, color, dataTest, font, size, numeric, weight, ...rest }, ref) => {
    const classes = {
      [styles.fontCondensed]: font === 'condensed',
      [styles.fontDefaultAlt]: font === 'defaultalt',
      [styles.fontSemiCondensed]: font === 'semicondensed',
      [styles.fontCondensedAlt]: font === 'condensedalt',
      [styles.sizeSmall]: size === 'small',
      [styles.sizeMinimum]: size === 'minimum',
      [styles.sizeMedium]: size === 'medium',
      [styles.sizeLarge]: size === 'large',
      [styles.sizexLarge]: size === 'xlarge',
      [styles.sizexxLarge]: size === 'xxlarge',
      [styles.sizexxxLarge]: size === 'xxxlarge',
      [styles.sizeMaximum]: size === 'maximum',
      [styles.sizeDisplay]: size === 'display',
      [styles.sizeDisplayLarge]: size === 'displaylarge',
      [styles.fontBold]: font === 'bold',
      [styles.weightThin]: weight === 'thin',
      [styles.weightLight]: weight === 'light',
      [styles.weightBook]: weight === 'book',
      [styles.weightRegular]: weight === 'regular',
      [styles.weightMedium]: weight === 'medium',
      [styles.weightSemiBold]: weight === 'semibold',
      [styles.weightBold]: weight === 'bold',
      [styles.numericTabular]: numeric,
      [styles.colorLight]: color === 'light',
      [styles.colorWhite]: color === 'white',
      [styles.colorLink]: color === 'link',
      [styles.colorSuccess]: color === 'success',
      [styles.colorError]: color === 'error',
      [styles.colorWarning]: color === 'warning',
      [styles.colorDisabled]: color === 'disabled'
    };

    const HtmlElement = as ? as : 'span';

    return (
      <HtmlElement
        ref={ref as React.LegacyRef<HTMLParagraphElement>}
        data-test={dataTest}
        className={classnames(styles.text, classes, className)}
        {...rest}
      >
        {children}
      </HtmlElement>
    );
  }
);

Text.displayName = 'Text';
Text.defaultProps = {
  size: 'default',
  font: 'default',
  color: 'default',
  numeric: false
};
